/* Section-driven public pages: Home, About, Founder, Girl Power, Privacy.
   Each reads a page's sections from Firestore and renders them via SectionRenderer. */
import { Helmet } from "react-helmet-async";
import { Link } from "../../lib/router-compat.jsx";
import PublicShell from "../../components/shell/PublicShell.jsx";
import SectionRenderer from "../../components/SectionRenderer.jsx";
import { Skeleton, Card, SmartImage, Icon } from "../../ui/index.js";
import { usePage, usePrograms, useSiteSettings } from "../../data/hooks.js";

function PageSkeleton() {
  return (
    <div className="container" style={{ padding: "64px 0", display: "flex", flexDirection: "column", gap: 24 }}>
      <Skeleton w="40%" h={20} />
      <Skeleton w="70%" h={48} />
      <Skeleton w="55%" h={24} />
      <Skeleton h={280} />
    </div>
  );
}

function SectionPage({ slug }) {
  const { page, sections, loading } = usePage(slug);
  return (
    <PublicShell>
      {page?.title && (
        <Helmet>
          <title>{page.title}</title>
          {page.description && <meta name="description" content={page.description} />}
        </Helmet>
      )}
      {loading ? <PageSkeleton /> : <SectionRenderer sections={sections} />}
    </PublicShell>
  );
}

export function HomePage() {
  const { page, sections, loading } = usePage("home");
  const { programs } = usePrograms({ publishedOnly: true });
  const featured = programs.filter((p) => !p.isFeatured).slice(0, 4);

  return (
    <PublicShell>
      <Helmet>
        <title>{page?.title || "Purple Angels Kids Foundation"}</title>
        {page?.description && <meta name="description" content={page.description} />}
      </Helmet>
      {loading ? (
        <PageSkeleton />
      ) : (
        <>
          {/* Render hero + first sections, then inject the programs strip before the CTA. */}
          <SectionRenderer sections={sections.filter((s) => s.type !== "cta")} />

          {featured.length > 0 && (
            <section className="section section--warm">
              <div className="container">
                <h2 className="t-3xl center" style={{ marginTop: 0, marginBottom: 32 }}>Our programs</h2>
                <div className="home-programs-grid">
                  {featured.map((p) => (
                    <Link key={p.slug} to={`/programs/${p.slug}`} style={{ textDecoration: "none", color: "inherit" }}>
                      <Card clickable>
                        <div style={{ aspectRatio: "16/9", borderRadius: "var(--radius-md)", overflow: "hidden", marginBottom: 16, background: "var(--bg-warm)" }}>
                          <SmartImage src={p.heroImage?.url} alt={p.heroImage?.alt || p.title} label={p.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </div>
                        <h3 className="t-xl" style={{ margin: "0 0 8px" }}>{p.title}</h3>
                        <p className="t-sm muted" style={{ margin: 0 }}>{p.summary}</p>
                      </Card>
                    </Link>
                  ))}
                </div>
                <div className="center" style={{ marginTop: 32 }}>
                  <Link to="/programs" className="btn btn--secondary">See all programs <Icon.ArrowRight size={16} /></Link>
                </div>
              </div>
            </section>
          )}

          <SectionRenderer sections={sections.filter((s) => s.type === "cta")} />
        </>
      )}
    </PublicShell>
  );
}

export function AboutPage() { return <SectionPage slug="about" />; }
export function FounderPage() { return <SectionPage slug="founder" />; }

export function GirlPowerPage() {
  const { page, sections, loading } = usePage("girl-power");
  return (
    <PublicShell>
      {page?.title && (
        <Helmet>
          <title>{page.title}</title>
          {page.description && <meta name="description" content={page.description} />}
        </Helmet>
      )}
      {loading ? <PageSkeleton /> : (
        <>
          <SectionRenderer sections={sections} />
          <section className="section section--warm">
            <div className="container">
              <Card variant="elevated" style={{ padding: 40, textAlign: "center", maxWidth: 720, margin: "0 auto" }}>
                <div className="icon-circle" style={{ margin: "0 auto 16px" }}><Icon.School size={24} /></div>
                <h2 className="t-3xl" style={{ marginTop: 0, marginBottom: 8 }}>Bring Girl Unlimited to your school</h2>
                <p className="t-base muted" style={{ margin: "0 auto 24px", maxWidth: 520 }}>
                  Schools can register a club to join the movement. Tell us about your group and our team will help you get set up.
                </p>
                <Link to="/register-club" className="btn btn--primary btn--lg">
                  Register a school club <Icon.ArrowRight size={18} />
                </Link>
              </Card>
            </div>
          </section>
        </>
      )}
    </PublicShell>
  );
}

export function PrivacyPage() {
  const { settings } = useSiteSettings();
  const updated = settings?.meta?.privacyLastUpdated;
  const { page, sections, loading } = usePage("privacy");
  return (
    <PublicShell>
      {page?.title && <Helmet><title>{page.title}</title></Helmet>}
      {loading ? (
        <PageSkeleton />
      ) : (
        <>
          {updated && (
            <div className="container" style={{ paddingTop: 24 }}>
              <span className="t-sm muted">Last updated: {updated}</span>
            </div>
          )}
          <SectionRenderer sections={sections} />
        </>
      )}
    </PublicShell>
  );
}

export default HomePage;
